import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { isAfter } from "date-fns";

import { formatDateAt } from "simplydo/core/dates";
import {
  DEADLINE_SPECIAL_FIELD_IDS,
  getDeadlineEditableFieldTitles,
  hasDeadlineFieldRestriction,
  isFieldEditableUnderDeadline,
} from "simplydo/core/ideas";
import {
  getSectionSubmissibility,
  getSubmissibilityColor,
  getSubmissibilityProgress,
  getSubmissibilitySectionKey,
  isSubmissibilitySectionComplete,
} from "simplydo/core/submissibility";
import type { OpenAPI } from "simplydo/interfaces";
import { MarkdownViewer, RichTextViewer } from "simplydo/react/richtext";
import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import {
  Button,
  Divider,
  Header,
  Icon,
  Label,
  Message,
  Popup,
  Segment,
} from "simplydo/react/ui";

import { useAppSelector } from "store";
import util from "utils/utils";

import { SideNavContainer } from "components/challenges/YourIdeas";
import { useAssessmentEditor } from "components/ideas/hooks/useAssessmentEditor";
import CircularProgress from "components/lib/CircularProgress";
import IPStatementInfo from "components/lib/IPStatementInfo";
import { SubmitIdeaButton } from "components/lib/Ideas";
import IdeaImpactStatement from "components/lib/Ideas/ImpactStatements/IdeaImpactStatement";
import { impactStageDefinitions } from "components/lib/Ideas/ImpactStatements/statementUtils";
import { SideNavLink } from "components/lib/SideNav";
import {
  BasicAccordion,
  PlaceholderItem,
  PlaceholderStack,
  Sticky,
  TooltipButton,
} from "components/lib/UI";

import IdeaAttachments from "../Attachments";
import IdeaComments from "../Comments";
import IdeaBusinessProfile from "../Components/IdeaBusinessProfile";
import IdeaSectionHeader from "../SectionHeader";
import { useChildIdState, useChildIdea, useIdea } from "../hooks/useIdea";
import type { IdeaExternalChanges } from "../types";
import useScrollTracking, { type ItemRefs } from "../useIdeaScrollTracking";
import { AssessorToolbar } from "./AssessorToolbar";
import Custom from "./Custom";
import IdeaNameField from "./Name";

const IdeaSection = styled.div`
  border-radius: 8px 8px 2px 2px;
  box-shadow: 0px 5px 10px 0px rgba(0, 0, 0, 0.1);
  @media print {
    page-break-after: always;
    margin: 2cm 30px;
  }

  &.is-assessment {
    border: 4px solid ${({ theme }) => theme.colorMap.violet};
    border-bottom-width: 3px;
    border-top-width: 3px;

    .ui.segment {
      border: none !important;
    }
  }
`;

const ChildIdeaChooser = styled.div`
  z-index: 999;
  > .title {
    opacity: 0.8;
    display: block;
    margin: 0 5px;
    font-weight: bold;
    margin-bottom: 5px;
  }
  > .buttons {
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    gap: 5px;
    .ui.button.merge-button-tab {
      width: 250px;
    }
    .button-idea-name {
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

const StickyMergeIdeaHeader = styled(Segment)`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px !important;
  .jump-to-section-button {
    margin-top: 5px;
    height: 25px;
    padding: 5px 12.5px;
  }
  ${ChildIdeaChooser} {
    width: 100%;
  }
`;

export type ChallengeIdea = OpenAPI.Schemas["ResponseGetIdea"];

type BodyProps = {
  idea?: ChallengeIdea;
  setSectionRef?: (ref: HTMLDivElement | null, key: string) => void;
  sectionRefs?: RefObject<ItemRefs>;
  setFieldRef?: (ref: HTMLDivElement | null, key: string) => void;
  fieldRefs?: RefObject<ItemRefs>;
  loading?: boolean;
  updateIdea: (value: unknown, path: string[], replace?: boolean) => void;
  scrollToRequired?: (sectionId: string) => void;
  onSubmit?: (toSubmit: boolean) => void;
  isEditing?: boolean;
  isSaving?: boolean;
  hideComments?: boolean;
  externalChanges?: IdeaExternalChanges;
  isViewingMergedIdea?: boolean;
  unsaved?: boolean;
  scrollToSection?: (sectionId: string) => void;
  scrollToField?: (fieldId: string) => void;
  emptyRequiredField?: boolean;
};

type BodyContainerProps = BodyProps & {
  scrollRef?: RefObject<HTMLDivElement | null>;
  removeIdeaFromMerge?: (parentId: string, childId: string) => void;
  removingIdeaFromMerge?: boolean;
};

const BodyContainer = ({
  scrollRef,
  removeIdeaFromMerge,
  removingIdeaFromMerge,
  setSectionRef,
  sectionRefs,
  setFieldRef,
  fieldRefs,
  ...rest
}: BodyContainerProps) => {
  /*
    Code for handling merged idea bodies

    One idea is set as the "active" idea, and the content from that idea will be what is shown
  */
  const [idea] = useIdea();
  const location = useLocation();

  const [ideaOrChild, ideaOrChildQuery] = useChildIdea();
  const [childId, setChildId] = useChildIdState();

  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();
  const sectionScroll = useScrollTracking(sectionRefs, scrollRef, -180);
  const fieldScroll = useScrollTracking(fieldRefs, scrollRef, -180);

  const usingMergedIdea = useMemo(
    () => !!idea?.children?.length,
    [idea?.children],
  );

  const [prevSectionViewed, setPrevSectionViewed] = useState<string>("");
  const [isNearScrollTop, setIsNearScrollTop] = useState(true);

  useEffect(() => {
    const scrollElement = scrollRef?.current;
    const getScrollTop = () =>
      scrollElement ? scrollElement.scrollTop : window.scrollY;

    const handleScroll = () => {
      setIsNearScrollTop(getScrollTop() < 100);
    };

    handleScroll();

    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll, { passive: true });
      return () => scrollElement.removeEventListener("scroll", handleScroll);
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrollRef]);

  const changeActiveChildIdea = useCallback(
    (childIdea: OpenAPI.Schemas["Idea"]) => {
      if (scrollRef?.current) {
        scrollRef.current.scrollTop = 0;
      } else {
        window.scrollTo(0, 0);
      }
      setPrevSectionViewed(sectionScroll.activeItemId ?? "");
      setChildId(childIdea._id);
    },
    [sectionScroll.activeItemId, scrollRef, setChildId],
  );

  // You can jump to the section that you were viewing in the previous idea, if the section exists in the new idea, and it's not the first section
  const canJumpToSectionInIdea = useMemo(() => {
    if (!childId || prevSectionViewed === "") {
      return false;
    }
    const sectionIds = Object.keys(sectionRefs?.current ?? {});
    const prevSectionIndex = sectionIds.findIndex(
      (sectionId) => sectionId === prevSectionViewed,
    );
    return (
      sectionIds.length > 0 &&
      sectionIds.indexOf(prevSectionViewed) !== -1 &&
      prevSectionIndex !== 0
    );
  }, [childId, prevSectionViewed, sectionRefs]);

  const onScrollToPrevSection = useCallback(
    (sectionId: string) => {
      setPrevSectionViewed("");
      sectionScroll.scrollToItem(sectionId);
    },
    [sectionScroll],
  );

  const prevSectionName = useMemo(
    () =>
      idea?.ownerIdeaTemplate?.body?.find(
        (section) => section.id === prevSectionViewed,
      )?.name,
    [idea?.ownerIdeaTemplate?.body, prevSectionViewed],
  );

  if (usingMergedIdea) {
    if (!childId) {
      return null;
    }
    return (
      <div>
        <Sticky
          top={
            location.pathname.startsWith(`/ideas/`)
              ? idea?.isSubmitted
                ? 110
                : 150
              : 5
          }
          bottom={undefined}
          style={{ zIndex: 100 }}
        >
          <StickyMergeIdeaHeader>
            <ChildIdeaChooser>
              <span className="title">
                Viewing merged {t("generic.idea")}...
              </span>
              <div className="buttons">
                {idea?.ownerChildren?.map((childIdea) => (
                  <Button.Group key={childIdea._id}>
                    <Button
                      active={childIdea._id === childId}
                      key={childIdea._id}
                      onClick={() => changeActiveChildIdea(childIdea)}
                      className="merge-button-tab"
                    >
                      <Button.Content>
                        <div
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <span className="button-idea-name">
                            {childIdea.name}
                          </span>
                          {childIdea?.owner ? (
                            <span style={{ opacity: 0.5, fontSize: "0.8em" }}>
                              By {childIdea?.owner?.profile?.fullName}
                            </span>
                          ) : null}
                        </div>
                      </Button.Content>
                    </Button>
                    {(util.canManageChallenge(user!, idea?.ownerChallenge) ||
                      util.hasPermission(
                        user!,
                        "challenge.mergeIdeas",
                        idea?.challenge,
                      )) &&
                    removeIdeaFromMerge ? (
                      <Popup
                        on="hover"
                        trigger={
                          <Button
                            icon="trash"
                            onClick={() =>
                              removeIdeaFromMerge(idea?._id, childIdea._id)
                            }
                          />
                        }
                        content={`Remove this ${t("generic.idea")} from the merge`}
                      />
                    ) : null}
                  </Button.Group>
                ))}
              </div>
              {idea?.ownerChildren?.length !== idea?.children?.length ? (
                <Message info size="tiny">
                  You are not able to view all {t("generic.ideas")} in this
                  merge. You may not have permission to view some of the{" "}
                  {t("generic.ideas")}, or they may have been deleted.
                </Message>
              ) : null}
            </ChildIdeaChooser>
            {canJumpToSectionInIdea && isNearScrollTop ? (
              <div>
                <Button
                  icon="arrow circle down"
                  content={`Jump to ${!prevSectionName ? "previous section" : `section "${prevSectionName}"`}`}
                  onClick={() => onScrollToPrevSection(prevSectionViewed)}
                  className="jump-to-section-button"
                  size="tiny"
                />
              </div>
            ) : null}
          </StickyMergeIdeaHeader>
        </Sticky>
        {ideaOrChild &&
        user?._id &&
        (ideaOrChild.user === user._id ||
          ideaOrChild.collaborators?.includes(user._id)) &&
        !(idea?.user === user._id) ? (
          <Message info>
            Your {t("generic.idea")} has been merged with other{" "}
            {t("generic.ideas")}; You are currently viewing the merge result.
            You can still view your individual {t("generic.idea")}{" "}
            <Link to={`/ideas/${childId}`}>here</Link>.
          </Message>
        ) : null}
        <Body
          setSectionRef={setSectionRef}
          setFieldRef={setFieldRef}
          isViewingMergedIdea
          scrollToSection={sectionScroll.scrollToItem}
          scrollToField={fieldScroll.scrollToItem}
          {...rest}
        />
        {!removingIdeaFromMerge ? (
          <div>
            <div ref={(ref) => setSectionRef?.(ref, "comments")} id="comments">
              <Divider hidden />
              <Header as="h4">Comments</Header>
              <IdeaComments forId={idea?._id} isViewingMergedIdea />
              <Divider hidden />
              <Divider hidden />
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <>
      {ideaOrChildQuery.isLoading || !ideaOrChild || !ideaOrChild._id ? null : (
        <>
          <AssessorToolbar
            isEditing={rest.isEditing}
            activeItemId={fieldScroll.activeItemId}
          />
        </>
      )}
      <Body
        setSectionRef={setSectionRef}
        setFieldRef={setFieldRef}
        scrollToSection={sectionScroll.scrollToItem}
        {...rest}
      />
    </>
  );
};

const Body = (props: BodyProps) => {
  const {
    setSectionRef,
    setFieldRef,
    updateIdea,
    scrollToRequired,
    onSubmit,
    isEditing,
    isSaving,
    unsaved,
    hideComments,
    externalChanges,
    isViewingMergedIdea,
    scrollToSection,
  } = props;
  const [idea, ideaQuery] = useChildIdea();
  const user = useAppSelector((state) => state.user);
  const theme = useTheme();
  const { t } = useTranslation();
  const firstAssessmentSectionIndex = idea?.ownerIdeaTemplate?.body?.findIndex(
    (section) => section.type === "assessment",
  );

  const {
    isAssessingSelectedRound,
    selectedAssessmentRoundId,
    urlAssessmentRoundId,
    assessmentState,
    submitAssessment,
    assessmentIsSubmissable,
    isSavingAssessment,
  } = useAssessmentEditor(idea);
  const hasCompletedConflictDeclaration =
    assessmentState?.conflictOfInterest?.hasConflictOfInterest !== undefined;

  const canEdit = util.canEditIdea(user!, idea);
  const isAuthor = util.isIdeaAuthor(user!, idea);
  const canViewEdi = util.hasPermission(
    user!,
    "org.viewEdi",
    idea?.ownerChallenge?.organisation,
  );
  const canBypassDeadlineFieldLock =
    util.hasPermission(user!, "challenge.editIdeas", idea?.challenge) ||
    util.hasPermission(
      user!,
      "org.manageChallenges",
      idea?.ownerChallenge?.organisation,
    );
  const isDeadlineFieldEditable = (fieldId: string) =>
    isFieldEditableUnderDeadline(idea?.submissionDeadline, fieldId, {
      bypass: canBypassDeadlineFieldLock,
    });
  const showAttachments =
    idea?.ownerIdeaTemplate?.enableAttachments &&
    ((canEdit && isEditing) ||
      (idea?.attachments && idea?.attachments?.length > 0));
  const showComments =
    (!isEditing || idea?.commentCount) &&
    !idea?.ownerChallenge?.preventIdeaComments &&
    !hideComments &&
    idea?._id &&
    (!isViewingMergedIdea || (isViewingMergedIdea && !idea?.parent));

  const assessmentFeedback = useMemo(() => {
    const items = ((idea as any)?.assessmentFeedback || []) as Array<any>;
    if (!items.length) return [];

    const rounds = idea?.ownerIdeaTemplate?.assessments?.rounds || [];
    const roundNameById = new Map(rounds.map((r) => [r.id, r.name]));

    const orderedRoundIds = rounds.map((r) => r.id);
    const extraRoundIds = Array.from(
      new Set(items.map((i) => i.assessmentRoundId).filter(Boolean)),
    ).filter((id) => !orderedRoundIds.includes(id));

    const allRoundIds = [...orderedRoundIds, ...extraRoundIds];
    return allRoundIds
      .map((roundId) => {
        const entry = items.find((i) => i.assessmentRoundId === roundId);
        if (!entry) return null;
        return {
          roundId,
          roundName: roundNameById.get(roundId) || "Assessment feedback",
          entry,
        };
      })
      .filter(Boolean) as Array<{
      roundId: string;
      roundName: string;
      entry: any;
    }>;
  }, [idea]);

  const userEstimateImpactSummary = useMemo(() => {
    return idea?.impactSummaries?.userEstimate?.summary?.trim() || "";
  }, [idea?.impactSummaries]);

  const showAutomatedSummary =
    util.hasPermission(user!, "super.viewDashboard") &&
    idea?.ownerChallenge?.aiIdeaSummarisation &&
    (idea?.generalSummary || userEstimateImpactSummary);

  return (
    <>
      {isAuthor && assessmentFeedback.length ? (
        <div>
          {assessmentFeedback.map(({ roundId, roundName, entry }) => (
            <Message key={roundId} style={{ marginBottom: 24 }}>
              <BasicAccordion
                title={
                  <Message.Header>
                    {t("common:capitalise", { key: "generic.idea" })} assessment
                    feedback - {roundName}
                  </Message.Header>
                }
                content={
                  <>
                    <Divider fitted style={{ marginBottom: "0.75em" }} />
                    <RichTextViewer value={entry.feedback || ""} />
                  </>
                }
              />
            </Message>
          ))}
        </div>
      ) : null}
      {idea?.ipStatement?.ipStatementEnabled ? (
        <IPStatementInfo ipStatement={idea?.ipStatement} />
      ) : null}
      {showAutomatedSummary ? (
        <Message
          size="small"
          style={{
            boxShadow:
              "0px 5px 10px 0px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(34,36,38,.22) inset",
          }}
        >
          <Message.Header
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            Automated {t("generic.idea")} summary
            <Popup
              offset={[13, 2]}
              trigger={
                <Icon
                  name="question circle"
                  size="small"
                  style={{ color: "#666" }}
                />
              }
              content={`This is an automated summary of this ${t("generic.idea")} based on its own content and the ${t("generic.challenge")} brief. It does not represent an assessment or critique.`}
            />
          </Message.Header>
          <Divider fitted style={{ marginTop: 4, marginBottom: 4 }} />

          {idea?.generalSummary ? (
            <MarkdownViewer value={idea.generalSummary} />
          ) : null}

          {userEstimateImpactSummary ? (
            <>
              <Divider fitted style={{ marginTop: 12, marginBottom: 8 }} />
              <Header as="h5" style={{ margin: "0 0 8px" }}>
                User {t("generic.impact")} summary
              </Header>
              <MarkdownViewer value={userEstimateImpactSummary} />
            </>
          ) : null}
        </Message>
      ) : null}

      {idea?.ownerChallenge?.ideaAnonymity && canEdit ? (
        <Message
          info
          icon="user secret"
          header="You are working anonymously"
          content={`${t("common:capitalise", { key: "generic.ideas" })} in this ${t("generic.challenge")} have their identity hidden to anyone who is not ${t("generic.challengeWithArticle")} administrator. Only you and ${t("generic.challenge")} administrators will be able to see that you created this ${t("generic.idea")}.`}
        />
      ) : null}

      {!idea?.isSubmitted && canEdit && idea?.submissionDeadline ? (
        <Message
          info
          icon="calendar"
          header={t("ideas.deadline.banner.header")}
          content={
            <>
              <p>
                {t("ideas.deadline.banner.until", {
                  deadline: formatDateAt(idea?.submissionDeadline.deadline),
                })}
              </p>
              {idea?.submissionDeadline.note ? (
                <p>
                  {t("ideas.deadline.banner.note", {
                    note: idea.submissionDeadline.note,
                  })}
                </p>
              ) : null}
              {hasDeadlineFieldRestriction(idea.submissionDeadline) ? (
                <>
                  <p>{t("ideas.deadline.banner.editableFields")}</p>
                  <ul>
                    {getDeadlineEditableFieldTitles(
                      idea.ownerIdeaTemplate,
                      idea.submissionDeadline.editableFields,
                      t,
                    ).map((title) => (
                      <li key={title}>{title}</li>
                    ))}
                  </ul>
                </>
              ) : null}
            </>
          }
        />
      ) : null}

      {ideaQuery.isLoading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 30 }}>
          <IdeaSection>
            <Segment>
              <PlaceholderStack gap={20}>
                <PlaceholderItem height={30} fluid />
                <PlaceholderItem height={150} fluid />
              </PlaceholderStack>
            </Segment>
          </IdeaSection>

          {Array(3)
            .fill(0)
            .map((_, index) => (
              <IdeaSection key={index}>
                <Segment attached="top" style={{ margin: 0 }}>
                  <PlaceholderItem height={30} fluid />
                </Segment>
                <Segment attached="bottom" style={{ margin: 0 }}>
                  <PlaceholderStack gap={10}>
                    <PlaceholderItem height={40} fluid />
                    <PlaceholderItem height={40} fluid />
                    <PlaceholderItem height={40} fluid />
                  </PlaceholderStack>
                </Segment>
              </IdeaSection>
            ))}
        </div>
      ) : null}

      {idea ? (
        <IdeaNameField
          isEditingName={
            !!isEditing &&
            isDeadlineFieldEditable(DEADLINE_SPECIAL_FIELD_IDS.name)
          }
          isEditingCover={
            !!isEditing &&
            isDeadlineFieldEditable(DEADLINE_SPECIAL_FIELD_IDS.coverImage)
          }
          ideaId={idea._id}
          ideaName={idea.name}
          ideaCoverImage={util.ideaCoverImage(idea, "2560x400")}
          updateIdea={updateIdea}
          challenge={idea?.ownerChallenge}
        />
      ) : null}
      {idea?.ownerIdeaTemplate?.body?.map(
        (
          section: NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number],
          sectionIndex: number,
        ) => {
          if (!util.shouldShowIdeaSection(idea, section)) return null;
          if (
            section.type === "edi" &&
            !util.shouldShowIdeaEdiSection(user!, idea, section)
          ) {
            return null;
          }
          if (section.type === "assessment") {
            if (
              isAssessingSelectedRound &&
              (!assessmentState ||
                !hasCompletedConflictDeclaration ||
                assessmentState?.conflictOfInterest?.hasConflictOfInterest)
            ) {
              return null;
            }
            if (!isAssessingSelectedRound && !urlAssessmentRoundId) {
              return null;
            }

            const sectionAssessmentRoundId =
              section.assessmentRoundId || "legacy";
            if (
              sectionAssessmentRoundId !==
              (selectedAssessmentRoundId || "legacy")
            ) {
              return null;
            }
          }
          if (section.type === "impact") {
            return (
              <>
                <IdeaSection
                  key={section.id}
                  ref={(ref) => setSectionRef?.(ref, "impactStatement")}
                  style={{
                    boxShadow:
                      isEditing &&
                      idea?.submissibilityStatus?.detail?.impactStatement
                        ?.incorrectFields?.length
                        ? "0.5px 0.5px 5px rgb(233 11 11 / 94%)"
                        : undefined,
                    borderRadius: 5,
                  }}
                >
                  <IdeaSectionHeader
                    isAssessor={isAssessingSelectedRound}
                    canViewEdi={false}
                    canEdit={false}
                    isAuthor={false}
                    section={{
                      id: "impactStatement",
                      name: `${section?.name ?? ""}${
                        isEditing &&
                        idea?.ownerChallenge?.impactStatements?.requireOnIdea
                          ? " *"
                          : ""
                      }`,
                      fields: [],
                    }}
                  />
                  <Segment attached>
                    <IdeaImpactStatement
                      forStage="userEstimate"
                      editable={
                        !!isEditing &&
                        isDeadlineFieldEditable(
                          DEADLINE_SPECIAL_FIELD_IDS.impactStatement,
                        )
                      }
                    />
                  </Segment>
                </IdeaSection>
                <Divider hidden />
              </>
            );
          }
          return (
            <div style={{ marginBottom: 30 }} key={section.id}>
              <IdeaSection
                className={cx(
                  sectionIndex === firstAssessmentSectionIndex &&
                    "assessment-tour-step-4",
                  section.type === "assessment" && "is-assessment",
                )}
                id={section.name}
                ref={(ref) => setSectionRef?.(ref, section.id)}
              >
                <IdeaSectionHeader
                  section={section}
                  assessment={assessmentState}
                  canViewEdi={canViewEdi}
                  canEdit={canEdit}
                  isAuthor={isAuthor}
                />
                <Segment attached>
                  {section.type === "businessProfile" ? (
                    <IdeaBusinessProfile
                      isEditing={
                        !!isEditing &&
                        isDeadlineFieldEditable(
                          DEADLINE_SPECIAL_FIELD_IDS.businessProfile,
                        )
                      }
                      updateIdea={updateIdea}
                      setFieldRef={setFieldRef}
                    />
                  ) : (
                    <Custom
                      section={section}
                      isEditing={isEditing}
                      updateIdea={updateIdea}
                      externalChanges={externalChanges}
                      emptyRequiredField={props.emptyRequiredField}
                      setFieldRef={setFieldRef}
                    />
                  )}
                </Segment>
              </IdeaSection>
            </div>
          );
        },
      )}

      {isAssessingSelectedRound ? (
        <div style={{ marginBottom: 30 }}>
          {assessmentState?.isSubmitted ? (
            <Label color="green" content="Assessment submitted" />
          ) : (
            <TooltipButton
              style={{ marginLeft: 3 }}
              content={
                assessmentState?.conflictOfInterest?.hasConflictOfInterest ===
                true
                  ? "Submit assessment with conflict of interest"
                  : "Submit assessment"
              }
              primary
              onClick={submitAssessment}
              disabled={!assessmentIsSubmissable}
              tooltip={
                assessmentIsSubmissable
                  ? undefined
                  : "To submit an assessment you either have to complete all sections of the assessment or declare a conflict of interest"
              }
              loading={isSavingAssessment}
            />
          )}
        </div>
      ) : null}

      {!theme.sizes.isComputer ? (
        <SideNavContainer style={{ marginBottom: 20 }}>
          <Header>Completion</Header>
          {idea?.ownerIdeaTemplate?.body?.map(
            (
              section: NonNullable<
                OpenAPI.Schemas["IdeaTemplate"]["body"]
              >[number],
              idx: number,
            ) => {
              const sectionName = section.name ?? "";
              if (!util.shouldShowIdeaSection(idea, section)) {
                return null;
              }
              if (section.type === "assessment" && !assessmentState) {
                return null;
              }
              const submissibility = getSectionSubmissibility(idea, section);
              const color = getSubmissibilityColor(submissibility);

              return (
                <SideNavLink
                  key={`${sectionName}-${idx}`}
                  content={
                    sectionName.charAt(0).toUpperCase() + sectionName.slice(1)
                  }
                  rightIcon={
                    submissibility && color ? (
                      <CircularProgress
                        size={16}
                        color={color}
                        progress={getSubmissibilityProgress(submissibility)}
                        strokeWidth={1.5}
                        icon={<Icon name="check" color="green" />}
                        iconVisible={isSubmissibilitySectionComplete(
                          submissibility,
                        )}
                      />
                    ) : undefined
                  }
                  onClick={() =>
                    scrollToSection?.(getSubmissibilitySectionKey(section))
                  }
                />
              );
            },
          )}
        </SideNavContainer>
      ) : null}

      {idea?.parent || isAssessingSelectedRound || idea?.children ? null : (
        <SubmitIdeaButton
          updateIdea={updateIdea}
          onSubmit={onSubmit ?? (() => {})}
          isSaving={isSaving}
          unsaved={unsaved}
        />
      )}

      {showAttachments ? (
        <div>
          <div
            ref={(ref) => setSectionRef?.(ref, "attachments")}
            id="attachments"
          >
            <Divider section hidden />
            <IdeaAttachments isEditing={isEditing} updateIdea={updateIdea} />
          </div>
        </div>
      ) : null}
      {showComments ? (
        <div>
          <div
            ref={(ref) => setSectionRef?.(ref, "comments")}
            id="idea-comments"
          >
            <Divider hidden />
            <Header as="h4">{t("generic.comments")}</Header>
            <IdeaComments forId={idea?._id} />
          </div>
        </div>
      ) : null}
    </>
  );
};

export default BodyContainer;
